import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Loading, Empty } from "../components/Layout";
import VendorSuggestions from "../components/VendorSuggestions";
import { VENDOR_DOMAIN_CATEGORIES } from "../lib/constants";
import { Store, Plus, X, Search, Trash2, MessageSquarePlus, Settings2 } from "lucide-react";

const CAT_BADGE = {
  Software: "bg-[#3E8F99]/15 text-[#3E8F99]",
  "Talent/Staffing": "bg-[#E2711D]/15 text-[#E2711D]",
  Trainer: "bg-[#7A5CB2]/15 text-[#7A5CB2]",
  "Training Vendor": "bg-[#2F7A3E]/15 text-[#2F7A3E]",
  Other: "bg-[#94A3AD]/20 text-[#1C2530]/60",
};

export function VendorForm({ meta, onSaved, initial, compact }) {
  const [f, setF] = useState(initial
    ? { name: initial.name || "", email: initial.email || "", category: initial.category || "Software", domain: initial.domain || "", context: initial.context || "" }
    : { name: "", email: "", category: "Software", domain: "", context: "" });
  const [busy, setBusy] = useState(false);
  const needsDomain = VENDOR_DOMAIN_CATEGORIES.includes(f.category);
  const submit = async () => {
    if (!f.name.trim()) { toast.error("Vendor name is required"); return; }
    if (needsDomain && !f.domain) { toast.error(`Domain is required for ${f.category}`); return; }
    setBusy(true);
    try {
      const payload = { ...f, domain: needsDomain ? f.domain : null, email: f.email || null };
      if (initial?.id) { await api.patch(`/vendors/${initial.id}`, payload); toast.success("Vendor updated"); }
      else { await api.post("/vendors", payload); toast.success("Vendor added to directory"); }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const inp = "w-full border border-[#DFDAD0] px-3 py-2 text-sm outline-none focus:border-[#1C2530]";
  return (
    <div className="space-y-3" data-testid="vendor-form">
      <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Vendor name *" data-testid="vendor-name" className={inp} />
      <input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Email" data-testid="vendor-email" className={inp} />
      <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value, domain: "" })} data-testid="vendor-category" className={inp + " bg-white"}>
        {meta.categories.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      {needsDomain && (
        <select value={f.domain} onChange={(e) => setF({ ...f, domain: e.target.value })} data-testid="vendor-domain" className={inp + " bg-white"}>
          <option value="">Select training domain *</option>
          {meta.domains.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
      )}
      <textarea value={f.context} onChange={(e) => setF({ ...f, context: e.target.value })} placeholder="Context note — how we know them, what they offer…" data-testid="vendor-context" rows={compact ? 2 : 3} className={inp} />
      <button onClick={submit} disabled={busy} data-testid="vendor-submit" className="w-full bg-[#1C2530] text-[#F5F3EE] py-2.5 text-sm font-medium hover:bg-[#2a3644] disabled:opacity-50">
        {busy ? "Saving…" : initial?.id ? "Save changes" : "Add vendor"}
      </button>
    </div>
  );
}

function ActivityLog({ vendor, onAdded }) {
  const [kind, setKind] = useState("note");
  const [text, setText] = useState("");
  const add = async () => {
    if (!text.trim()) { toast.error("Add some detail"); return; }
    try { await api.post(`/vendors/${vendor.id}/activity`, { kind, text }); setText(""); onAdded(); toast.success("Logged"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const log = [...(vendor.activity_log || [])].reverse();
  return (
    <div data-testid="vendor-activity">
      <div className="flex items-center gap-2 mb-3">
        <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="activity-kind" className="border border-[#DFDAD0] px-2 py-2 text-xs bg-white outline-none">
          {["note", "call", "email", "meeting"].map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
        <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Log an interaction…" data-testid="activity-text" className="flex-1 border border-[#DFDAD0] px-3 py-2 text-sm outline-none focus:border-[#1C2530]" />
        <button onClick={add} data-testid="activity-add" className="flex items-center gap-1 bg-[#E50914] text-white px-3 py-2 text-xs font-medium hover:bg-[#c40810]"><MessageSquarePlus className="w-3.5 h-3.5" /> Log</button>
      </div>
      <div className="space-y-2 max-h-72 overflow-y-auto">
        {log.map((a, i) => (
          <div key={i} className="border-l-2 border-[#DFDAD0] pl-3 py-1" data-testid="activity-row">
            <div className="flex items-center gap-2 font-mono text-[9px] uppercase text-[#1C2530]/50">
              <span className="text-[#3E8F99]">{a.kind}</span> · {a.actor} · {new Date(a.at).toLocaleString()}
            </div>
            <div className="text-sm text-[#1C2530] mt-0.5">{a.text}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Vendors() {
  const { user } = useAuth();
  const [vendors, setVendors] = useState(null);
  const [meta, setMeta] = useState(null);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showDomains, setShowDomains] = useState(false);
  const [newDomain, setNewDomain] = useState("");

  const load = () => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (catFilter) p.set("category", catFilter);
    return api.get(`/vendors?${p.toString()}`).then((r) => setVendors(r.data));
  };
  const loadMeta = () => api.get("/vendor-domains").then((r) => setMeta(r.data));
  useEffect(() => { loadMeta(); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, catFilter]);
  const openDetail = (v) => api.get(`/vendors/${v.id}`).then((r) => setSelected(r.data));

  const addDomain = async () => {
    if (!newDomain.trim()) return;
    try { await api.post("/vendor-domains", { name: newDomain }); setNewDomain(""); await loadMeta(); toast.success("Domain added"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (v) => {
    if (!window.confirm(`Delete vendor "${v.name}"?`)) return;
    try { await api.delete(`/vendors/${v.id}`); setSelected(null); load(); toast.success("Vendor deleted"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <>
      <PageHeader eyebrow="EXTERNAL PARTNERS · NOT IN PIPELINE" title="Vendor Directory" hint="External partners you can bring into a deal (trainers, content studios, platforms). They never enter the pipeline — attach them to an offering or proposal when needed."
        right={<button onClick={() => setShowAdd(true)} data-testid="add-vendor-btn" className="flex items-center gap-1.5 bg-[#E50914] text-white px-4 py-2 text-sm font-medium rounded-sm hover:bg-[#c40810]"><Plus className="w-4 h-4" /> Add Vendor</button>} />
      <div className="p-8 space-y-5" data-testid="vendors-page">
        <VendorSuggestions meta={meta} onAccepted={load} />
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#1C2530]/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, context…" data-testid="vendor-search" className="w-full border border-[#DFDAD0] pl-9 pr-3 py-2 text-sm outline-none focus:border-[#1C2530]" />
          </div>
          <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)} data-testid="vendor-cat-filter" className="border border-[#DFDAD0] px-3 py-2 text-sm bg-white outline-none">
            <option value="">All categories</option>
            {meta?.categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          {user.role === "admin" && (
            <button onClick={() => setShowDomains((s) => !s)} aria-pressed={showDomains} data-testid="manage-domains-btn" className={`flex items-center gap-1.5 border px-3 py-2 text-xs font-mono uppercase hover:bg-[#EFECE5] ${showDomains ? "bg-[#1C2530] text-[#F5F3EE] border-[#1C2530]" : "border-[#DFDAD0]"}`}><Settings2 className="w-3.5 h-3.5" /> Domains</button>
          )}
        </div>

        {showDomains && user.role === "admin" && (
          <div className="border border-[#DFDAD0] bg-white p-4" data-testid="domains-panel">
            <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60 mb-2">Training domains (Trainer / Training Vendor)</div>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {meta?.domains.map((d) => <span key={d} className="px-2 py-1 bg-[#EFECE5] text-[11px] font-mono">{d}</span>)}
            </div>
            <div className="flex gap-2">
              <input value={newDomain} onChange={(e) => setNewDomain(e.target.value)} placeholder="New domain name" data-testid="new-domain-input" className="flex-1 border border-[#DFDAD0] px-3 py-2 text-sm outline-none focus:border-[#1C2530]" />
              <button onClick={addDomain} data-testid="add-domain-btn" className="bg-[#1C2530] text-[#F5F3EE] px-4 py-2 text-sm hover:bg-[#2a3644]">Add</button>
            </div>
          </div>
        )}

        {!vendors ? <Loading /> : vendors.length === 0 ? <Empty label="NO_VENDORS_YET" /> : (
          <div className="border border-[#DFDAD0] bg-white overflow-hidden" data-testid="vendors-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#EFECE5] font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">
                  <th className="text-left px-4 py-2.5">Vendor</th>
                  <th className="text-left px-4 py-2.5">Category</th>
                  <th className="text-left px-4 py-2.5">Domain</th>
                  <th className="text-left px-4 py-2.5">Email</th>
                  <th className="text-right px-4 py-2.5">Activity</th>
                </tr>
              </thead>
              <tbody>
                {vendors.map((v, i) => (
                  <tr key={v.id} onClick={() => openDetail(v)} data-testid={`vendor-row-${v.id}`} className={`cursor-pointer hover:bg-[#EFECE5] border-t border-[#DFDAD0] ${i % 2 ? "bg-[#F5F3EE]/40" : "bg-white"}`}>
                    <td className="px-4 py-2.5 font-medium text-[#1C2530]">{v.name}</td>
                    <td className="px-4 py-2.5"><span className={`font-mono text-[9px] uppercase px-2 py-1 ${CAT_BADGE[v.category] || CAT_BADGE.Other}`}>{v.category}</span></td>
                    <td className="px-4 py-2.5 text-[#1C2530]/70 text-xs">{v.domain || "—"}</td>
                    <td className="px-4 py-2.5 text-[#1C2530]/70 text-xs">{v.email || "—"}</td>
                    <td className="px-4 py-2.5 text-right font-mono tabular-nums text-[#1C2530]/60">{v.activity_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showAdd && meta && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="add-vendor-modal">
          <div className="bg-white border border-[#DFDAD0] w-full max-w-md p-6 rounded-md">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">No lead score · no pipeline stage</div>
                <h3 className="text-lg font-semibold text-[#1C2530] mt-1">New Vendor</h3>
              </div>
              <button onClick={() => setShowAdd(false)} className="text-[#1C2530]/50 hover:text-[#1C2530]"><X className="w-5 h-5" /></button>
            </div>
            <VendorForm meta={meta} onSaved={() => { setShowAdd(false); load(); }} />
          </div>
        </div>
      )}

      {selected && meta && (
        <div className="fixed inset-0 z-50 flex items-center justify-end bg-black/50" data-testid="vendor-detail" onClick={() => setSelected(null)}>
          <div className="bg-[#F5F3EE] w-full max-w-lg h-full overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <span className={`font-mono text-[9px] uppercase px-2 py-1 ${CAT_BADGE[selected.category] || CAT_BADGE.Other}`}>{selected.category}{selected.domain ? ` · ${selected.domain}` : ""}</span>
                <h3 className="text-xl font-semibold text-[#1C2530] mt-2 flex items-center gap-2"><Store className="w-5 h-5" /> {selected.name}</h3>
                <div className="text-sm text-[#1C2530]/60">{selected.email || "no email"}</div>
              </div>
              <div className="flex items-center gap-2">
                {user.role === "admin" && <button onClick={() => del(selected)} data-testid="vendor-delete-btn" className="border border-[#B23A2E]/40 text-[#B23A2E] p-2 hover:bg-[#B23A2E]/5"><Trash2 className="w-4 h-4" /></button>}
                <button onClick={() => setSelected(null)} className="text-[#1C2530]/50 hover:text-[#1C2530]"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <div className="border border-[#DFDAD0] bg-white p-4 mb-4">
              <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60 mb-2">Edit details</div>
              <VendorForm meta={meta} initial={selected} compact onSaved={() => { openDetail(selected); load(); }} />
            </div>
            <div className="border border-[#DFDAD0] bg-white p-4">
              <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60 mb-3">Activity log</div>
              <ActivityLog vendor={selected} onAdded={() => openDetail(selected)} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
